import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Trans, useTranslation } from '@packlink/translation-provider';
import { PaymentDetails } from '@common/components/PaymentDetails/PaymentDetails';
import { DestinationDetails } from './components/DestinationDetails/DestinationDetails';
import { OriginDetails } from './components/OriginDetails/OriginDetails';
import { OrderDetails } from './components/OrderDetails/OrderDetails';
import { PostSaleTracking } from './components/PostsaleTracking/PostSaleTracking';
import { ShipmentDetailsAddress } from './components/ShipmentDetailsAddress/ShipmentDetailsAddress';
import { ShipmentDetailsEmailWarning } from './components/ShipmentDetailsNotifications/ShipmentDetailsEmailWarning';
import { ShipmentDetailsPackagingAdvice } from './components/ShipmentDetailsNotifications/ShipmentDetailsPackagingAdvice';
import { ShipmentDetailsCarrierWarnings } from './components/ShipmentDetailsNotifications/ShipmentDetailsCarrierWarnings';
import { ShipmentDetailsPacklinkReference } from './components/ShipmentDetailsPacklinkReference/ShipmentsDetailsPacklinkReference';
import { PostsaleSection } from './components/PostsaleSection/PostsaleSection';
import { PrintInStoreDisclaimer } from '@Postsale/pages/ShipmentDetails/components/ShipmentDetailsContent/components/ShipmentDetailsNotifications/PrintInStoreDisclaimer';
import { CustomsDisclaimer } from '@Postsale/pages/ShipmentDetails/components/ShipmentDetailsContent/components/ShipmentDetailsNotifications/CustomsDisclaimer';
import { useSourceShortcode } from '@common/hooks/useSourceShortcode';
import { Address, IAddress, IPrice, IPriceDetail, ProShipment } from '@packlink/packlink-sdk';
import { ShipmentUtils } from '@utils/ShipmentUtils';
import {
    getDropoffMapDividerStyles,
    getDropoffMapStyles,
    getShowDropoffMapBtnStyles,
    getGridElementStyles,
    getShipmentDetailsEmailAndPackagingStyles,
    getShipmentDetailsContentStyles,
    getShipmentDetailsNotificationStyles,
} from './ShipmentDetailsContentStyles';
import { DropOff } from '@packlink/cartography';
import { useAllDropoffPoints } from '../../../../hooks/useAllDropoffPoints';
import { Button, ButtonVariant, Divider, DividerVariant, Next } from '@shipengine/giger';
import { getMapMarker } from '@utils/cdn';
import { useAmplitude } from '@common/hooks';
import { AmplitudeEvents } from '@constants/amplitude';
import { SubscriptionNotification } from '@common/components/SubscriptionNotification/SubscriptionNotification';
import { APP_ROUTE } from '@router/routes';
import { useLastClientSubscription } from '@Subscriptions/hooks/useClientSubscriptions';
import { useSimulatedPriceByShipment } from '@Postsale/hooks/useSimulatedPriceByShipment';

type ShipmentDetailsContentProps = {
    shipment: ProShipment;
};

export function ShipmentDetailsContent({ shipment, ...props }: ShipmentDetailsContentProps): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { isClientNonSubscribed } = useLastClientSubscription();
    const { title: trackingSectionTitle } = usePostsaleTrackingData(shipment);
    const { title: originDetailsSectionTitle, address: origin } = usePostsaleOriginDetailsData(shipment);
    const { title: destinationDetailsSectionTitle, address: destination } = usePostsaleDestinationDetailsData(shipment);
    const { title: orderDetailsSectionTitle } = usePostsaleOrderDetailsData(shipment);
    const paymentDetailsProps = usePostsalePaymentDetailsData(shipment);
    const hasDropoffOrigin = shipment.data.service?.dropOff?.origin;
    const [isDropoffMapSelected, setDropoffMap] = useState(false);

    const { dropoffPoints } = useAllDropoffPoints(
        isDropoffMapSelected,
        origin,
        shipment.data.service?.id?.toString() as string,
    );
    const { discountedPrice, showDiscountedPrice, isLoadingSimulatedPrice, paidPrice } = useSimulatedPriceByShipment(
        shipment.data.packlinkReference,
        isClientNonSubscribed,
    );

    const { sendAmplitudeClickEvent } = useAmplitude();

    const handleDropoffMap = () => {
        setDropoffMap((prevState) => !prevState);
    };

    const handleOpenDropoffMap = () => {
        sendAmplitudeClickEvent(AmplitudeEvents.POSTSALE_PUDO_MAP);
        handleDropoffMap();
    };

    const goToUpgradeSubscription = () => {
        sendAmplitudeClickEvent(AmplitudeEvents.SUBSCRIPTION_UPGRADE_PRICE_POST_SALE);
        navigate(APP_ROUTE.SUBSCRIPTIONS.INDEX);
    };

    return (
        <section css={getShipmentDetailsContentStyles} {...props}>
            {showCustomsNotification(shipment) && (
                <CustomsDisclaimer
                    css={getGridElementStyles('customs')}
                    data-id="shipment-details-content-customs-notification"
                />
            )}

            <ShipmentDetailsCarrierWarnings
                css={getGridElementStyles('carrierWarnings')}
                shipment={shipment}
                data-id="shipment-details-content-carrier-warnings"
            />

            {showPrintInStoreWarning(shipment) && (
                <PrintInStoreDisclaimer
                    css={getGridElementStyles('printerless')}
                    data-id="shipment-details-print-in-store-disclaimer"
                />
            )}

            <PostsaleSection
                title={t('post-sale.details.reference-number')}
                tooltip={t('post-sale.details.reference-number-for-contact')}
                css={getGridElementStyles('tracking1')}
            >
                <ShipmentDetailsPacklinkReference shipment={shipment} />
            </PostsaleSection>

            <PostsaleSection title={trackingSectionTitle} css={getGridElementStyles('tracking2')}>
                <PostSaleTracking shipment={shipment} />
            </PostsaleSection>

            <PostsaleSection title={originDetailsSectionTitle} css={getGridElementStyles('origin')}>
                <OriginDetails shipment={shipment} data-id="shipment-details-content-origin-details" />
                {!isDropoffMapSelected && hasDropoffOrigin && (
                    <Button
                        variant={ButtonVariant.OUTLINED}
                        onClick={handleOpenDropoffMap}
                        css={getShowDropoffMapBtnStyles}
                    >
                        {t('post-sale.details.destination-see-map')}
                    </Button>
                )}
            </PostsaleSection>

            <PostsaleSection title={destinationDetailsSectionTitle} css={getGridElementStyles('destination')}>
                <DestinationDetails shipment={shipment} data-id="shipment-details-content-destination-details" />
            </PostsaleSection>

            {isDropoffMapSelected && hasDropoffOrigin && (
                <PostsaleSection css={[getDropoffMapStyles, getGridElementStyles('pudo')]}>
                    <DropOff addresses={dropoffPoints} markerIcon={getMapMarker()} />
                    <Divider variant={DividerVariant.GRAY} css={getDropoffMapDividerStyles} />

                    <Button onClick={handleDropoffMap} variant={ButtonVariant.TEXT}>
                        {t('post-sale.details.destination-close-map')}
                    </Button>
                </PostsaleSection>
            )}
            <PostsaleSection css={getGridElementStyles('sender')}>
                <ShipmentDetailsAddress address={origin} data-id="shipment-details-content-origin-address-data" />
            </PostsaleSection>

            <PostsaleSection css={getGridElementStyles('recipient')}>
                <ShipmentDetailsAddress
                    address={destination}
                    data-id="shipment-details-content-destination-address-data"
                />
            </PostsaleSection>

            <PostsaleSection css={[getShipmentDetailsEmailAndPackagingStyles, getGridElementStyles('warning1')]}>
                <ShipmentDetailsPackagingAdvice data-id="shipment-details-content-packaging-advice" />
            </PostsaleSection>

            <PostsaleSection css={[getShipmentDetailsEmailAndPackagingStyles, getGridElementStyles('warning2')]}>
                <ShipmentDetailsEmailWarning address={destination} data-id="shipment-details-content-email-warning" />
            </PostsaleSection>

            <PostsaleSection title={orderDetailsSectionTitle} css={getGridElementStyles('order')}>
                <OrderDetails data-id="shipment-details-content-order-details" shipment={shipment} />
            </PostsaleSection>

            <PostsaleSection title={t('post-sale.invoice.title')} css={getGridElementStyles('price')}>
                {isClientNonSubscribed && showDiscountedPrice && !isLoadingSimulatedPrice && (
                    <SubscriptionNotification css={getShipmentDetailsNotificationStyles}>
                        <Trans
                            i18nKey="subscriptions.upgrade-notification.post-sale-price"
                            values={{ discountedPrice, paidPrice }}
                            components={[<Next.Link key="link" onClick={goToUpgradeSubscription} />]}
                            parent="span"
                        />
                    </SubscriptionNotification>
                )}
                <PaymentDetails data-id="shipment-details-content-payment-details" {...paymentDetailsProps} />
            </PostsaleSection>
        </section>
    );
}

function showPrintInStoreWarning(shipment: ProShipment) {
    return !!shipment.data?.upsales?.printInStore?.available;
}

function showCustomsNotification(shipment: ProShipment) {
    return !ShipmentUtils.isShipmentCanceled(shipment.data) && shipment.data.hasCustoms;
}

function usePostsaleTrackingData(shipment: ProShipment) {
    const { t } = useTranslation();

    const carrier = shipment.data.service?.carrierName;
    return {
        title: `${carrier} ${t('post-sale.carrier.tracking')}`,
    };
}

function usePostsaleOrderDetailsData(shipment: ProShipment) {
    const { t } = useTranslation();

    const source = useSourceShortcode(shipment.data.source);
    const title = `${source} ${t('post-sale.order.title')}`;

    return {
        title,
    };
}

function usePostsaleOriginDetailsData(shipment: ProShipment) {
    const { t } = useTranslation();
    const title = shipment.data.service?.dropOff?.origin
        ? t('post-sale.origin-details.dropoff-point')
        : t('post-sale.origin-details.collection');

    return {
        title,
        address: shipment.data.from as Address<IAddress>,
    };
}

function usePostsaleDestinationDetailsData(shipment: ProShipment) {
    const { t } = useTranslation();
    const hasEstimatedDeliveryDate = shipment.data.estimatedDeliveryDate && !shipment.data.service?.dropOff?.origin;
    const title = hasEstimatedDeliveryDate
        ? t('postsale-destination.estimated-delivery')
        : t('postsale-destination.delivery');

    return {
        title,
        address: shipment.data.to as Address<IAddress>,
    };
}

function usePostsalePaymentDetailsData(shipment: ProShipment) {
    return {
        shipments: [shipment],
        shipmentsPriceDetails: [shipment.data.price] as IPrice[],
        total: shipment.data.price?.price as IPriceDetail,
    };
}
